import React, { useState } from 'react';
import { Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import { collection, addDoc, query, where, getDocs, serverTimestamp, getFirestore } from 'firebase/firestore';
import firebaseApp from '../../../firebase/credenciales';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';

const AreaForm = ({ area, onSave, onCancel, ruc, empresa }) => {
    const [saving, setSaving] = useState(false);
    const db = getFirestore(firebaseApp);

    // Validación con Yup
    const validationSchema = Yup.object({
        nombre: Yup.string()
            .required('El nombre del área es obligatorio')
            .min(2, 'El nombre debe tener al menos 2 caracteres')
            .max(50, 'El nombre no debe exceder los 50 caracteres'),
        ceco: Yup.string()
            .required('El código CECO es obligatorio')
            .matches(/^[A-Za-z0-9]+$/, 'Solo se permiten letras y números')
            .max(20, 'El código CECO no debe exceder los 20 caracteres'),
        descripcion: Yup.string()
            .max(200, 'La descripción no debe exceder los 200 caracteres')
    });

    // Configuración de Formik
    const formik = useFormik({
        initialValues: {
            nombre: area?.nombre || '',
            ceco: area?.ceco || '',
            descripcion: area?.descripcion || '',
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                setSaving(true);

                if (area) {
                    // Si es edición, usamos el callback del componente padre
                    onSave(values);
                } else {
                    // Si es creación, verificamos primero que no exista
                    // Verificar si ya existe esta área
                    const areasRef = collection(db, 'areas');
                    
                    // Verificar nombre duplicado
                    const qNombre = query(
                        areasRef,
                        where('ruc', '==', ruc),
                        where('nombre', '==', values.nombre.trim())
                    );
                    const snapshotNombre = await getDocs(qNombre);
                    
                    if (!snapshotNombre.empty) {
                        Swal.fire({
                            icon: 'warning',
                            title: 'Nombre duplicado',
                            text: 'Ya existe un área con este nombre.'
                        });
                        setSaving(false);
                        return;
                    }
                    
                    // Verificar CECO duplicado
                    const qCeco = query(
                        areasRef,
                        where('ruc', '==', ruc),
                        where('ceco', '==', values.ceco.trim())
                    );
                    const snapshotCeco = await getDocs(qCeco);
                    
                    if (!snapshotCeco.empty) {
                        Swal.fire({
                            icon: 'warning',
                            title: 'CECO duplicado',
                            text: 'Ya existe un área con este código CECO.'
                        });
                        setSaving(false);
                        return;
                    }

                    // Crear nueva área
                    await addDoc(collection(db, 'areas'), {
                        ...values,
                        ruc,
                        empresa,
                        fechaCreacion: serverTimestamp()
                    });

                    onSave(values);
                }
            } catch (error) {
                console.error("Error guardando área:", error);
                
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Ocurrió un error al guardar el área.'
                });
            } finally {
                setSaving(false);
            }
        }
    });

    return (
        <Form onSubmit={formik.handleSubmit}>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Nombre del Área *</Form.Label>
                        <Form.Control
                            type="text"
                            name="nombre"
                            placeholder="Ej: Recursos Humanos"
                            {...formik.getFieldProps('nombre')}
                            isInvalid={formik.touched.nombre && !!formik.errors.nombre}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.nombre}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                            Ingrese el nombre del área o departamento
                        </Form.Text>
                    </Form.Group>
                </Col>
                
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Centro de Costo (CECO) *</Form.Label>
                        <Form.Control
                            type="text"
                            name="ceco"
                            placeholder="Ej: RH001"
                            {...formik.getFieldProps('ceco')}
                            isInvalid={formik.touched.ceco && !!formik.errors.ceco}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.ceco}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                            Ingrese el código de centro de costo sin espacios ni caracteres especiales
                        </Form.Text>
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Descripción</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    name="descripcion"
                    placeholder="Descripción opcional del área..."
                    {...formik.getFieldProps('descripcion')}
                    isInvalid={formik.touched.descripcion && !!formik.errors.descripcion}
                />
                <Form.Control.Feedback type="invalid">
                    {formik.errors.descripcion}
                </Form.Control.Feedback>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2 mt-4">
                <Button 
                    variant="secondary"
                    onClick={onCancel}
                    disabled={saving}
                >
                    Cancelar
                </Button>
                <Button 
                    variant="primary"
                    type="submit"
                    disabled={saving}
                >
                    {saving ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Guardando...
                        </>
                    ) : (
                        'Guardar'
                    )}
                </Button>
            </div>
        </Form>
    );
};

export default AreaForm;